import withRouter from "Components/Common/withRouter"
import ConsultantFields from "Components/organisms/forms/contractor-form/types"
import ConsultantForm from "Components/organisms/forms/contractor-form"
import FormPageTemplate from "Components/templates/form-page-template"
import ActionButton from "Components/atoms/buttons/action-button"
import AreYouSureButtons from "Components/molecule/are-you-sure-buttons"
import Modal from "Components/molecule/modal"
import { ConsultantValidation, PotentialConsultantValidation } from "data/consultants-form"
import { FormikHelpers, useFormik } from "formik"
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { getEmployee, getEmployeeFormFieldValues, submitConsultantForm, submitEditConsultantForm, submitEditPotentialConsultantForm } from "slices/thunk"
import Employee from 'models/employee';
import { NavigateFunction, useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import useManual from "hooks/useManual"
import { EmployeesSliceState, setEmployees } from "slices/human-resources/employees/reducer"
import { EmployeeType } from "models/employee-type"
import { HelpIcon } from "Components/atoms/icons"
import { toDepartmentOptions } from "utils/department-options"

const selectEmployees = createSelector(
    (state: any) => state.Employees,
    ({
        jobTitles,
        officeLocations,
        managers,
        departments,
        status,
        employeeTypes,
        genders,
        employees
    }: EmployeesSliceState) => ({
        jobTitles,
        officeLocations,
        managers,
        departments,
        status,
        employeeTypes,
        genders,
        employees
    })
)

const CONSULTANT_TYPE = "Consultant"
const POTENTIAL_CONSULTANT_TYPE = "Potential Consultant"

const EditConsultantPage = (props: any) => {
    document.title = "Edit Consultants | IbexIQ";
    const { handleOpenManual } = useManual("human-resources/consultants");
    const dispatch: any = useDispatch();
    const [showAreYouSureModal, setShowAreYouSureModal] = useState<boolean>(false)
    const [employee, setEmployee] = useState<Employee | null>(null);
    const [employeeTypeId, setEmployeeTypeId] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [searchParams] = useSearchParams();

    const {
        jobTitles,
        officeLocations,
        managers,
        departments,
        status,
        employeeTypes,
        genders,
        employees,
    } = useSelector(selectEmployees);


    const isPotentialConsultant = useMemo(() => {
        const selected = employeeTypes.find(e => e.id === employeeTypeId)
        return selected?.name === POTENTIAL_CONSULTANT_TYPE
    }, [employeeTypeId, employeeTypes]);

    // Employee type can't actually be changed via this form — the API ignores it on edit.
    const disabledFields = useMemo(() => ({
        employeeTypeId: true,
    }), []);

    const initializePage = async (consultantId: string, navigate: NavigateFunction) => {
        setIsLoading(true)

        if (
            jobTitles.length === 0 ||
            genders.length === 0 ||
            managers.length === 0 ||
            departments.length === 0 ||
            status.length === 0 ||
            officeLocations.length === 0 ||
            employeeTypes.length === 0
        ) {
            await dispatch(getEmployeeFormFieldValues())
        }

        const consultant = await getEmployee(consultantId);

        if (!consultant) {
            toast.error("Something went wrong. Could not find consultant")
            navigate(-1);
            return;
        }


        setEmployee(consultant);
        setIsLoading(false)
    }

    useEffect(() => {
        const consultantId = searchParams.get("consultantId");

        if (!consultantId) {
            toast.error("Something went wrong. Consultant Id not found");
            props.router.navigate(-1)
        } else {
            initializePage(consultantId, props.router.navigate);
        }
    }, [searchParams, props.router.navigate, setEmployee])

    const handleSubmitConsultantForm = async (values: ConsultantFields, helpers: FormikHelpers<ConsultantFields>) => {
        helpers.setSubmitting(false)
        setShowAreYouSureModal(true)
    }

    const initialValues: ConsultantFields = useMemo(() => ({
        employeeCode: employee?.employeeCode ?? "",
        firstName: employee?.firstName ?? "",
        middleName: "",
        lastName: employee?.lastName ?? "",
        commonName: employee?.commonName ?? "",
        genderId: employee?.genderId ?? "",
        companyEmail: employee?.companyEmail ?? "",
        phoneNumber: employee?.phoneNumber ?? "",
        jobTitleId: employee?.jobTitleId ?? "",
        managerId: employee?.managerId ?? "",
        departmentId: employee?.departmentId ?? "",
        officeLocationId: employee?.locationId ?? "",
        statusId: employee?.statusId ?? "",
        employeeTypeId: employee?.typeId ?? "",
        hireDate: employee?.hireDate?.toString() ?? "",
        terminationDate: employee?.terminatedAt?.toString() ?? "",
        contractEndDate: employee?.contractEndDate?.toString() ?? "",
    }), [employee])

    const {
        values,
        errors,
        touched,
        handleSubmit,
        handleBlur,
        handleChange,
        isValid,
        handleReset,
        isSubmitting,
        setSubmitting
    } = useFormik({
        onSubmit: handleSubmitConsultantForm,
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: isPotentialConsultant ? PotentialConsultantValidation : ConsultantValidation,
    })

    // We need to track when employee type changes so we can switch the validation.
    useEffect(() => {
        setEmployeeTypeId(values.employeeTypeId ?? null)
    }, [values.employeeTypeId])

    const handleBack = () => {
        props.router.navigate(-1)
        setShowAreYouSureModal(false)
    }

    const handleModalClose = () => setShowAreYouSureModal(false);

    const hanldeConfirmSubmitConsultantForm = useCallback(async (consultantId: string, values: ConsultantFields, setSubmitting: (val: boolean) => any, reset: (e: any) => void) => {
        setSubmitting(true);
        const res = isPotentialConsultant ? await submitEditPotentialConsultantForm(consultantId, values, () => reset({})) : await submitEditConsultantForm(consultantId, values, () => reset({}));
        setSubmitting(false)

        if (!res) {
            handleModalClose()
        } else {
            res.employeeType = {} as EmployeeType;
            res.employeeType.name = isPotentialConsultant ? POTENTIAL_CONSULTANT_TYPE : CONSULTANT_TYPE
            dispatch(setEmployees(employees.map(e => e.id === res.id ? res : e)))
            handleBack()
        }
    }, [handleBack, handleModalClose, isPotentialConsultant, employees, dispatch])


    return <FormPageTemplate
        title="Human Resources"
        breadcrumbItem="Edit Consultant"
        isLoading={isLoading}
        loadingTitle="Loading Consultant"
        loadingSubtitle="Fetching consultant information…"
        actions={<ActionButton onClick={handleOpenManual}>
                Help <HelpIcon />
            </ActionButton>
        }
        form={<>
            <div>
                <h4>Edit Consultant</h4>
                <h6>Please fill out the following fields and click submit.</h6>
            </div>
            <ConsultantForm
                edit
                genders={genders.map(g => ({ value: g.id, label: g.description }))}
                jobTitles={jobTitles.map(jt => ({ value: jt.id, label: jt.description }))}
                managers={managers.map(e => ({ value: e.id ?? '', label: e.firstName + " " + e.lastName }))}
                departments={toDepartmentOptions(departments)}
                officeLocations={officeLocations.map(o => ({ value: o.id, label: o.description }))}
                status={status.map(s => ({ label: s.description, value: s.id }))}
                employeeTypes={employeeTypes.filter(e => e.name === CONSULTANT_TYPE || e.name === POTENTIAL_CONSULTANT_TYPE).map(e => ({ label: e.name, value: e.id }))}
                values={values}
                errors={errors}
                touched={touched}
                isSubmitting={isSubmitting}
                isValid={isValid}
                potentialConsultant={isPotentialConsultant}
                disabledFields={disabledFields}
                handleChange={handleChange}
                handleBlur={handleBlur}
                handleSubmit={handleSubmit}
                handleCancel={handleBack}
            />
        </>}
        modal={<Modal
            isOpen={showAreYouSureModal}
            onClose={handleModalClose}
            title={"Confirm"}
            subtitle={"Are you sure want to edit consultant?"}
        >
            <AreYouSureButtons
                disable={isSubmitting}
                acceptButtonStyle={{ width: '150px' }}
                declineButtonStyle={{ width: '150px' }}
                handleAccept={() => hanldeConfirmSubmitConsultantForm(searchParams.get("consultantId")!, values, setSubmitting, handleReset)}
                handleDecline={handleModalClose}
                acceptText={isSubmitting ? "Editting..." : "Edit"}
                declineText={"Cancel"}
            />
        </Modal>}
    />
}

export default withRouter(EditConsultantPage);